/**
 * Slider hero Shape (sections/shape-hero.liquid).
 * L'autoplay est piloté par l'animation CSS de la barre de progression de la slide active :
 * quand elle se termine (animationend), on passe à la suivante. Mettre en pause revient
 * à mettre l'animation en pause (classe .is-paused), la reprise repart là où elle s'était arrêtée.
 */
class ShapeHero extends HTMLElement {
  #index = 0;
  /** Pauses automatiques en cours (survol, focus, onglet masqué, hors écran) */
  #holds = new Set();
  #userPaused = false;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {number | null} */
  #swipeStartX = null;
  #swipeStartY = 0;

  connectedCallback() {
    this.slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.shape-hero__slide')]);
    this.steps = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('.shape-hero__step')]);
    if (this.slides.length < 2) return;

    this.autoplay = this.hasAttribute('data-autoplay') && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.autoplay) this.removeAttribute('data-autoplay');

    this.steps.forEach((step) => step.addEventListener('click', () => this.go(Number(step.dataset.index))));
    this.querySelector('[data-action="prev"]')?.addEventListener('click', () => this.go(this.#index - 1));
    this.querySelector('[data-action="next"]')?.addEventListener('click', () => this.go(this.#index + 1));
    this.querySelector('[data-action="toggle"]')?.addEventListener('click', this.#toggleUserPause);

    // Fin de la barre de progression = slide suivante
    this.addEventListener('animationend', (event) => {
      if (event.animationName === 'shape-hero-progress' && this.autoplay) this.go(this.#index + 1);
    });

    // Pauses automatiques : survol (souris), focus clavier dans le slider, onglet masqué, hors écran
    this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.#hold('hover', true));
    this.addEventListener('pointerleave', () => this.#hold('hover', false));
    this.addEventListener('focusin', () => this.#hold('focus', true));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) this.#hold('focus', false);
    });
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.#observer = new IntersectionObserver(([entry]) => this.#hold('offscreen', !entry.isIntersecting), {
      threshold: 0.2,
    });
    this.#observer.observe(this);

    // Clavier : flèches gauche / droite quand le focus est dans le slider
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.#index - 1);
      if (event.key === 'ArrowRight') this.go(this.#index + 1);
    });

    // Swipe horizontal sur écran tactile
    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      this.#swipeStartX = event.clientX;
      this.#swipeStartY = event.clientY;
    });
    this.addEventListener('pointerup', (event) => {
      if (this.#swipeStartX === null) return;
      const dx = event.clientX - this.#swipeStartX;
      const dy = event.clientY - this.#swipeStartY;
      this.#swipeStartX = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.#index + (dx < 0 ? 1 : -1));
    });
    this.addEventListener('pointercancel', () => (this.#swipeStartX = null));

    // Dans l'éditeur de thème : afficher la slide sélectionnée et arrêter le défilement
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.slides.indexOf(/** @type {HTMLElement} */ (event.target));
      if (index >= 0) this.go(index);
      this.#hold('editor', true);
    });
    this.addEventListener('shopify:block:deselect', () => this.#hold('editor', false));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  /** @param {number} target */
  go(target) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    if (next === this.#index) return;

    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
      slide.querySelectorAll('a').forEach((link) => (link.tabIndex = active ? 0 : -1));
      slide.querySelector('video')?.[active ? 'play' : 'pause']?.();
    });

    this.steps.forEach((step, i) => {
      step.setAttribute('aria-selected', String(i === next));
      step.classList.toggle('is-done', i < next);
    });

    this.#index = next;
  }

  /**
   * @param {string} reason
   * @param {boolean} on
   */
  #hold(reason, on) {
    if (on) this.#holds.add(reason);
    else this.#holds.delete(reason);
    this.#updatePaused();
  }

  #toggleUserPause = () => {
    this.#userPaused = !this.#userPaused;
    this.classList.toggle('is-user-paused', this.#userPaused);
    const toggle = /** @type {HTMLElement | null} */ (this.querySelector('[data-action="toggle"]'));
    if (toggle) {
      toggle.setAttribute('aria-label', (this.#userPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause) ?? '');
    }
    this.#updatePaused();
  };

  #updatePaused() {
    this.classList.toggle('is-paused', this.#userPaused || this.#holds.size > 0);
  }

  #onVisibility = () => this.#hold('hidden', document.hidden);
}

if (!customElements.get('shape-hero')) {
  customElements.define('shape-hero', ShapeHero);
}
